import { finalize } from 'rxjs/operators';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { BannerNotificationsService, PaginatedList, SpinnerService } from '../../../xform-compat';
import { AddCompanyService, CompanyService, FinancialService } from '../../../core/data-services/company';
import { ExchangeService, TenantService } from '../../../core/data-services';
import { EventsHubService } from '../../../core/events-hub.service';
import { CompanyBasic, CompanyCategory, CompanyModel, ExchangeItem, Tenant } from '../../../shared/models';
import { CONFIG } from '../../../config';
import { DomainService } from '../../../core/data-services/domain.service';
import { Domain } from '../../../shared/models/domain.model';
import { SecurityIndex } from 'src/app/shared/models/company/security-index.model';
import { NgSelectComponent } from '@ng-select/ng-select';
import { CompanyInfoService } from '../../../core/data-services/company/company-info.service';
import { CompanyInfo } from '../../../shared/models/company/company-info.model';
import { CompanyInfoType } from '../../../shared/models/company/company-info-type.enum';
import { TaraService } from '../../../core/data-services/ai/tara.service';

/**
 * Component Class for the Create new Company Modal
 */
@Component({
  selector: 'rms-add-company',
  templateUrl: './add-company.component.html',
  styleUrls: ['./add-company.component.scss'],
  standalone: false,
})
export class AddCompanyComponent implements OnInit, OnDestroy {

  private modalSubscription: Subscription;
  private modalReference: NgbModalRef;
  private largePageSize = CONFIG.pagination.singleLargePage;
  private redirect = true;
  @ViewChild('addCompanyModal', { static: true }) modal: ElementRef;
  public exchangeList: PaginatedList<ExchangeItem>;
  public companyCategories: PaginatedList<CompanyCategory>;
  public tenantList: PaginatedList<Tenant>;
  public isPrivate: boolean | null;
  public addManualPublic = false;
  public selectedSecurity: SecurityIndex;
  public categoryId: number;
  public exchangeId: number;
  public tenantId: number;
  public name: string;
  public ticker: string;
  public subdomain: string;
  public domains: Array<Domain> = [];
  public platform: string;
  public mapsForLead: string;
  public currency: string;
  public currencyList: Array<{ code: string, name: string }>;

  public readonly DEFAULT_TENANT_ID = CONFIG.defaultTenantId;
  public readonly domainRelevances = ['DEFAULT', 'COMPANY'];

  constructor(
    private addCompanyService: AddCompanyService,
    private exchangeService: ExchangeService,
    private tenantService: TenantService,
    private _financialService: FinancialService,
    private spinnerService: SpinnerService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService,
    private eventsHubService: EventsHubService,
    private modalService: NgbModal,
    private companyService: CompanyService,
    private router: Router,
    private domainService: DomainService,
    private readonly companyInfoService: CompanyInfoService,
    private readonly taraService: TaraService,
  ) {
    this.isPrivate = null;
    this.ticker = '';
    this.subdomain = '';
    this.platform = '';
    this.mapsForLead = '';
  }

  /**
   * On Init, get the exchanges list and subscribes to the create company modal
   */
  ngOnInit() {
    this.modalSubscription = this.eventsHubService.onAddCompanyModalOpens$.subscribe(
      (redirect: boolean) => {
        this.ticker = '';
        this.addManualPublic = false;
        this.redirect = redirect;
        this.modalReference = this.modalService.open(this.modal, {size: 'lg'});
        this.modalReference.result.then(
          () => this.name = null,
          () => this.name = null);
        this.getExchanges();
        this.getCategories();
        this.getTenants();
        this.getCurrencies();
        this.isPrivate = null;
        this.subdomain = '';
        this.platform = '';
        this.mapsForLead = '';
        this.domains = [];
        this.selectedSecurity = null;
      }
    );
  }

  /**
   * Unsubscribe on destroy component
   */
  ngOnDestroy () {
    this.modalSubscription.unsubscribe();
  }

  /**
   * Creates a new company
   * @param {NgForm} form
   */
  public createRecord (form: NgForm) {
    if (form.invalid) {
      return;
    }
    this.spinnerService.start();

    const publicFields = {
      securityIndex: this.selectedSecurity && this.selectedSecurity.fsymRegionalId,
      ticker: form.value.ticker || this.ticker,
      exchangeId: form.value.exchangeId || this.exchangeId,
      currency: form.value.currency || this.currency,
    }

    this.addCompanyService.createRecord({
      ...form.value,
      ...(!this.isPrivate && publicFields),
      domains: this.domains,
      isPrivate: this.isPrivate,
    }).pipe(
      finalize(() => this.spinnerService.stop()))
      .subscribe(
        newCompany => {
          this.handleDomains(newCompany, this.domains);
          // If the user entered a description then save it, otherwise generate one via AI
          if (form.value.description) {
            this.updateCompanyInfo(newCompany.id, CompanyInfoType.DESCRIPTION, form.value.description);
          } else {
            this.generateCompanyDescription(newCompany);
          }
          this.bannerNotificationService.success(this.translateService.instant('MESSAGES.CREATED', {value: 'Company'}));
          form.reset();
          this.modalReference.close();
          if (this.redirect) {
            const urlTree = this.router.createUrlTree(['/company', newCompany.id]);
            const url = this.router.serializeUrl(urlTree);
            window.open(url, '_blank');
          }
        },
        error => this.bannerNotificationService.error(error)
      );
  }

  onSecuritySelected = (event: SecurityIndex) => {
    if (event) {
      this.name = event.properName;
      this.ticker = event.tickerRegion.split("-")[0];
      this.currency=event.currency;
      this.selectedSecurity = event;
    }
  }

  createPublicManually = (select: NgSelectComponent) => {
    this.addManualPublic = true;
    select.close();
  }

  public formInvalid(form: NgForm) {
    if (form.invalid) {
      return true;
    }

    const hasName = !!this.name;
    const isDefaultTenant = this.tenantId === CONFIG.defaultTenantId;
    const hasNoDomains = !this.domains || this.domains.length === 0;
    const isPublicWithSecurity = !this.addManualPublic && this.selectedSecurity;
    const hasValidCompanyType = isPublicWithSecurity || this.isPrivate || this.addManualPublic;

    const isValid = hasName && !(isDefaultTenant && hasNoDomains) && hasValidCompanyType;
    return !isValid;
  }

  private handleDomains(createdCompany: CompanyModel, domains: Array<Domain>) {
    forkJoin([
      ...domains.map(a => this.domainService.addCompanyToDomain(createdCompany.id, a.id)),
    ]).subscribe(() => {}, error => this.bannerNotificationService.error(error));
  }

  public resetForm() {
      this.name = null;
      this.currency = null;
      this.selectedSecurity = null;
      this.addManualPublic = false;
      this.ticker = '';
      this.subdomain = '';
      this.platform = '';
      this.mapsForLead = '';
      this.setDefaultCategory();
      this.setDefaultExchange();
      this.setDefaultTenant();
      this.setDefaultCurrency();
  }

  private getCurrencies(): void {
    this._financialService.getCurrencies().subscribe(
      currencyList => {
        this.currencyList = currencyList;
        this.setDefaultCurrency();
      },
      error1 => this.bannerNotificationService.error(error1)
    );
  }

  private setDefaultCurrency(): void {
    this.currency = 'USD';
  }

  /**
   * Populates the exchange drop down in the form component.
   * NASDAQ is the default value.
   */
   private getExchanges () {
    this.exchangeService.getRecords({pagesize: this.largePageSize}).subscribe(
      value => {
        this.exchangeList = value;
        this.setDefaultExchange();
      },
      error => this.bannerNotificationService.error(error)
    );
  }

  private setDefaultExchange() {
    for (let i = 0; i < this.exchangeList.records.length; i++) {
      if (this.exchangeList.records[i].name === CONFIG.defaultExchangeName) {
        this.exchangeId = this.exchangeList.records[i].id;
        break;
      }
    }
  }

  /**
   * Populates the categories drop down in the form component.
   * First category getted is the default value.
   */
   private getCategories () {
    this.companyService.getCompanyCategories({pagesize: this.largePageSize}).subscribe(
      records => {
        this.companyCategories = records;
        this.setDefaultCategory();
      },
      error => this.bannerNotificationService.error(error)
    );
  }

  private setDefaultCategory() {
    this.categoryId = this.companyCategories.records[0].id;
  }

  /**
   * Populates the tenants drop down in the form component.
   * RA is the default value.
   */
   private getTenants () {
    this.tenantService.getRecords({pagesize: this.largePageSize}).subscribe(
      value => {
        value.records = value.records.map(t => new Tenant(t));
        this.tenantList = value;
        this.setDefaultTenant();
      },
      error => this.bannerNotificationService.error(error)
    );
  }

  private setDefaultTenant() {
      const defaultTenant = this.tenantList.records.find( tenant => tenant.id === CONFIG.defaultTenantId);
      this.tenantId = defaultTenant ? defaultTenant.id : this.tenantList.records[0].id;
  }

  private updateCompanyInfo(companyId: number, infoType: CompanyInfoType, value: any) {
     const info = new CompanyInfo({
       companyInfoType: infoType,
       value: value
     })
     this.companyInfoService.update(companyId, [info]).subscribe();
  }

  private generateCompanyDescription(company: CompanyBasic) {
     this.taraService.generateCompanyDescriptions([company.id]).subscribe();
  }

}
